// Confirmação de email (`spec/17-frontend.md` §8.1).
//
// O link do email aponta para a API (`GET /v1/me/auth/verify-email`, montada pelo Better
// Auth): ela valida o token no servidor e REDIRECIONA para cá, sem parâmetro quando deu
// certo, com `?error=<code>` quando o token é inválido ou expirou
// (`redirectOnError` em `better-auth/dist/api/routes/email-verification.mjs`). O front
// nunca chama a rota com `token`: é o navegador que a visita.
//
// O `?email=` vem do nosso `/signup` e é o que habilita o reenvio.

import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { api } from '../api/client.ts';
import { errorMessageOf } from '../api/errors.ts';
import { AuthShell, FormError } from '../components/app/auth-shell.tsx';
import { Button } from '../components/ui/button.tsx';

const searchSchema = z.object({
  email: z.string().optional(),
  error: z.string().optional(),
});

function VerifyEmail() {
  const { email, error } = Route.useSearch();

  const resend = useMutation({
    mutationFn: async () =>
      api('/v1/me/auth/send-verification-email', {
        method: 'POST',
        body: { email, callbackURL: `${window.location.origin}/verify-email` },
      }),
  });

  const resendButton = (
    <div className="flex flex-col gap-2">
      <Button
        variant="outline"
        onClick={() => {
          resend.mutate();
        }}
        disabled={resend.isPending || email === undefined}
      >
        {resend.isPending ? 'Enviando…' : 'Reenviar email'}
      </Button>
      {email === undefined && (
        <p className="text-11 text-muted">Entre com seu email para pedir um link novo.</p>
      )}
      {resend.isSuccess && <p className="text-11 text-green">Email reenviado.</p>}
      <FormError message={resend.isError ? errorMessageOf(resend.error) : undefined} />
    </div>
  );

  if (error !== undefined) {
    return (
      <AuthShell
        title="Link inválido ou expirado"
        description="Peça um link novo e abra o mais recente."
        footer={
          <Link to="/login" className="text-gold underline-offset-4 hover:underline">
            Voltar ao login
          </Link>
        }
      >
        {resendButton}
      </AuthShell>
    );
  }

  if (email !== undefined) {
    return (
      <AuthShell
        title="Confirme seu email"
        description={`Mandamos um link para ${email}. Abra o link para liberar o painel.`}
      >
        {resendButton}
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Email confirmado" description="Pronto. Agora é só entrar.">
      <Button asChild className="h-11 w-full">
        <Link to="/login">Ir para o login</Link>
      </Button>
    </AuthShell>
  );
}

export const Route = createFileRoute('/verify-email/')({
  validateSearch: searchSchema,
  component: VerifyEmail,
});
